import React, { useCallback, useEffect, useRef, useState } from 'react'
import { StyleSheet, Text } from 'react-native'

import useAddCustomerStore from '@zet/customer/src/store/addCustomer'
import { B2, Button, colors, Pressable, SH3, Stack } from '@zet/design-system'
import {
  privacyPolicyForExperianLink,
  termsAndConditionsForExperianLink,
} from '@zet/feature-brands/utils/constants'
import { i18n } from '@zet/localization'
import { zetApi } from '@zet/network'
import { KeyBoardWrapper } from '@zet/utils'
import { OTP_REGEX } from '@zet/utils/regularExpression'
import { useUnsafeVariable } from 'nativewind'
import {
  CodeField,
  Cursor,
  useBlurOnFulfill,
  useClearByFocusCell,
} from 'react-native-confirmation-code-field'

import { HeaderDetailsSection } from '../../components'
import { useAddCustomerState } from '../../hooks/useAddCustomer'
import useGetAgentDetailsAndTheme from '../../hooks/useGetAgentDetailsAndTheme'

const { locale = 'en' } = zetApi.defaults.headers.common

export const OtpVerification = () => {
  const [value, setValue] = useState<string>('')
  const [timer, setTimer] = useState<number>(30)
  const [disableResend, setDisableResend] = useState<boolean>(false)

  useGetAgentDetailsAndTheme()
  const [props, getCellOnLayoutHandler] = useClearByFocusCell({
    value,
    setValue,
  })
  const ref = useBlurOnFulfill({ value, cellCount: 4 })
  const { mutate, isLoading } = useAddCustomerState()

  const timerRef = useRef<NodeJS.Timeout | null>(null)
  const [colorPrimary100] = useUnsafeVariable('--color-primary-100')
  const { customerPhoneNumber, customerId } = useAddCustomerStore()

  const startTimer = useCallback(() => {
    setDisableResend(true)
    setTimer(30)
    timerRef.current = setInterval(() => {
      setTimer((prev) => {
        if (prev <= 1) {
          if (timerRef.current) clearInterval(timerRef.current)
          setDisableResend(false)
          return 0
        }
        return prev - 1
      })
    }, 1000)
  }, [])

  const getUrlParam = (param: any) =>
    new URLSearchParams(window.location.search).get(param)

  const getRequestBodyData = (extraData = {}) => ({
    phoneNumber: getUrlParam('customerPhoneNumber') || customerPhoneNumber,
    screenName: 'SHOWN_QUICK_OTP_SCREEN',
    ...extraData,
  })

  const resendOtp = () => mutate(getRequestBodyData())

  const handleSubmit = () => {
    const requestBodyData = getRequestBodyData({
      customerId: getUrlParam('customerId') || customerId,
      otp: value,
    })

    mutate(requestBodyData)
  }

  const onPressResendButton = () => {
    setValue('')
    startTimer()
    resendOtp()
  }

  function openPrivacyPolicy() {
    window.open(privacyPolicyForExperianLink)
  }
  function openTermsAndConditions() {
    window.open(termsAndConditionsForExperianLink)
  }

  useEffect(() => {
    startTimer()
    return () => {
      if (timerRef.current) clearInterval(timerRef.current)
    }
  }, [startTimer])

  return (
    <KeyBoardWrapper
      BottomSheet={
        <Stack className="border-t-grays-30 border-t px-3 py-2">
          <Button
            className="w-full"
            size={'lg'}
            style={{
              backgroundColor:
                value.length === 4 ? colorPrimary100 : colors.grays[50],
            }}
            disabled={!(value.length === 4 && OTP_REGEX.test(value))}
            onPress={handleSubmit}
            loading={isLoading}
            loaderText={i18n.t('customerDetails.verifying')}
          >
            Verify
          </Button>
        </Stack>
      }
    >
      <Stack className="px-3">
        <HeaderDetailsSection
          subTitle="Enter OTP sent to your mobile to complete verification"
          title="Please Verify"
        />

        <CodeField
          ref={ref}
          caretHidden={true}
          onChangeText={setValue}
          {...props}
          cellCount={4}
          value={value}
          rootStyle={styles.codeFieldRoot}
          keyboardType="number-pad"
          textContentType="oneTimeCode"
          renderCell={({ index, symbol, isFocused }) => (
            <Stack
              key={index}
              style={[styles.cell, isFocused && styles.focusCell]}
            >
              <Text onLayout={getCellOnLayoutHandler(index)}>
                {symbol || (isFocused ? <Cursor /> : null)}
              </Text>
            </Stack>
          )}
        />
        {!OTP_REGEX.test(value) && (
          <B2 className="text-error-100 mt-2 self-center">Invalid OTP</B2>
        )}

        {timer === 0 && !disableResend ? (
          <Pressable
            className="items-center"
            onPress={onPressResendButton}
            eventName="click_action"
            eventPayload={{
              item_name: 'add_bms_customer_resend_otp',
            }}
          >
            <SH3 className="text-primary-100">
              {i18n.t('webFlow.otpScreen.resend')}
            </SH3>
          </Pressable>
        ) : (
          <SH3 className="text-grays-60 text-center">
            {`Resend OTP in `}
            <SH3 className="text-primary-100">
              00:{timer < 10 ? `0${timer}` : timer}
            </SH3>
          </SH3>
        )}

        <Stack>
          <B2 className="mt-5">
            {i18n.t('verifyPhNum.agree')}
            <Pressable
              onPress={openTermsAndConditions}
              eventName="click_action"
              eventPayload={{
                item_name: 'add_bms_customer_terms_and_conditions',
              }}
            >
              <Text className="text-primary-100 font-[Graphie-Bold] text-sm leading-5">
                {' '}
                {i18n.t('verifyPhNum.terms')}
              </Text>
            </Pressable>
            <B2 className="text-primary-100">{i18n.t('verifyPhNum.and')}</B2>
            <Pressable
              onPress={openPrivacyPolicy}
              eventName="click_action"
              eventPayload={{
                item_name: 'add_bms_customer_privacy_policy',
              }}
            >
              <Text
                className={`text-primary-100 font-[Graphie-Bold] text-sm leading-5`}
              >
                {' '}
                {i18n.t('verifyPhNum.privacyPolicy')}.
              </Text>
            </Pressable>
            <B2>
              {i18n.t('verifyPhNum.agreeSuffix', {
                continue: locale === 'hi' ? i18n.t('verifyPhNum.continue') : '',
              })}
            </B2>
          </B2>
        </Stack>
      </Stack>
    </KeyBoardWrapper>
  )
}

const styles = StyleSheet.create({
  codeFieldRoot: {
    marginTop: 4,
    marginHorizontal: 58,
    alignItems: 'center',
    marginBottom: 20,
  },
  cell: {
    width: 55,
    height: 55,
    fontSize: 20,
    fontWeight: '500',
    borderWidth: 1,
    borderColor: colors.grays[40],
    textAlign: 'center',
    alignItems: 'center',
    justifyContent: 'center',
    borderRadius: 6,
    backgroundColor: '#F9F9FB',
  },
  focusCell: {
    borderColor: colors.primary[50],
  },
})
